import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import EditFacilityForm, { type FacilityFormValues } from './EditFacilityForm';
import { formatDateInputValue, type Facility } from './utils/utils';

interface FacilityDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    facility?: Facility | null;
    onSaved: (facility?: Facility) => void;
    viewMode: 'admin' | 'siteAdmin';
}

function getFacilityInitialValues(
    facility?: Facility | null
): Partial<FacilityFormValues> | undefined {
    if (!facility) return undefined;

    return {
        name: facility.name,
        licenses: facility.licenses,
        region: facility.region || '',
        featureAccess: facility.featureAccess || [],
        startDate: facility.start_date
            ? formatDateInputValue(facility.start_date)
            : '',
        endDate: facility.end_date
            ? formatDateInputValue(facility.end_date)
            : '',
        isTrial: facility.isTrial,
        address: facility.address
            ? {
                  street: facility.address.street || '',
                  number: facility.address.number || '',
                  postalCode: facility.address.postalCode
                      ? String(facility.address.postalCode)
                      : '',
                  city: facility.address.city || '',
                  country: facility.address.country || '',
              }
            : undefined,
    };
}

export default function FacilityDialog({
    open,
    onOpenChange,
    facility,
    onSaved,
    viewMode,
}: FacilityDialogProps) {
    const { t } = useTranslation();
    const isCreate = !facility;
    const initialValues = getFacilityInitialValues(facility);
    const formKey = facility?._id ?? 'new-facility';

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>
                        {isCreate ? t('facilities.formDialog.newFacility') : t('facilities.formDialog.editFacility')}
                    </DialogTitle>
                </DialogHeader>
                <EditFacilityForm
                    key={formKey}
                    mode={isCreate ? 'create' : 'edit'}
                    departmentId={facility?._id}
                    initialValues={initialValues}
                    saveMode={viewMode}
                    onSaved={(savedFacility) => {
                        onOpenChange(false);
                        onSaved(savedFacility);
                    }}
                />
            </DialogContent>
        </Dialog>
    );
}
